import {
  ActiveHoldDto,
  CarrierVisitDirection,
  HoldStatus,
  JourneyCategory,
  OrderResponseDto,
  OrderStatus,
  WeightClassResponseDto,
} from '@planning/app/api'
import { IContainerJourneyItem } from '@planning/pages/Issues/Stores/ContainerJourneyItem'
import { ContainerJourneyItemStore } from '@planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { IIssueItem } from '@planning/pages/Issues/Stores/IssueItem'
import { IssueItemStore } from '@planning/pages/Issues/Stores/IssueItemStore'
import _ from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import { EntityItem } from '../base/EntityItem'
import { IContainerItem } from '../container/ContainerItem'
import { ICompanyItem } from '../externalDriver/CompanyItem'
import { CompanyItemStore } from '../externalDriver/CompanyItemStore'
import { IOrderUpdateItem } from '../orderUpdate/OrderUpdateItem'
import { OrderUpdateItemStore } from '../orderUpdate/OrderUpdateItemStore'
import { IRailcarTrackPositionItem } from '../railTrack/RailcarTrackPositionItem'
import { RailcarTrackPositionItemStore } from '../railTrack/RailcarTrackPositionItemStore'
import { IRailVisitItem } from '../railVisit/RailVisitItem'
import { ITruckVisitItem } from '../truckVisit/TruckVisitItem'
import { IEntity } from '../types'
import { IVesselVisitItem } from '../vesselVisit/VesselVisitItem'
import { WeightClassItemStore } from '../weightClass/WeightClassItemStore'
import { IAssignedCraneForOrder } from './../tallyman/VesselTallyViewStore'
import { OrderItemStore } from './OrderItemStore'

export interface IOrderItem extends IEntity<OrderResponseDto> {
  visit: IVesselVisitItem | IRailVisitItem | ITruckVisitItem | undefined
  container: IContainerItem | undefined
  railcarTrackPosition: IRailcarTrackPositionItem | undefined
  notResolvedIssues?: IIssueItem[]
  hasNotResolvedIssues: boolean
  hasMissingPosition?: boolean
  hasActiveHold?: boolean
  activeHoldNames: string[]
  isNonNumericAndCannotBeFulfilled?: boolean
  unreadOrderUpdates?: IOrderUpdateItem[]
  containerJourney?: IContainerJourneyItem
  assignedCraneName?: string | null
  isAssignedCraneNotToSelected?: boolean
  shippingLine?: ICompanyItem
  shipper?: ICompanyItem
  forwarder?: ICompanyItem
  journeyCategory?: JourneyCategory
  weightClass?: WeightClassResponseDto
  setCrane?: (crane?: IAssignedCraneForOrder, selectedCraneId?: number) => void
  canUnassignFromRelease: boolean
}

export class OrderItem extends EntityItem<OrderResponseDto> implements IOrderItem {
  assignedCraneName?: string | null
  isAssignedCraneNotToSelected?: boolean

  constructor(
    private itemStore: OrderItemStore,
    private railcarTrackPositionStore: RailcarTrackPositionItemStore,
    private issueStore: IssueItemStore | undefined,
    private orderUpdateStore: OrderUpdateItemStore | undefined,
    private containerJourneyItemStore: ContainerJourneyItemStore | undefined,
    private companyStore: CompanyItemStore | undefined,
    private weightClassItemStore: WeightClassItemStore | undefined,
    id: number,
    data: OrderResponseDto,
  ) {
    super(id, data)
    makeObservable(this, {
      assignedCraneName: observable,
      isAssignedCraneNotToSelected: observable,
      visit: computed,
      container: computed,
      railcarTrackPosition: computed,
      notResolvedIssues: computed,
      hasNotResolvedIssues: computed,
      unreadOrderUpdates: computed,
      containerJourney: computed,
      hasMissingPosition: computed,
      hasActiveHold: computed,
      activeHoldNames: computed,
      isNonNumericAndCannotBeFulfilled: computed,
      journeyCategory: computed,
      weightClass: computed,
      canUnassignFromRelease: computed,

      setCrane: action,
    })
  }

  get weightClass() {
    const weight = this.data.vgm ?? this.data.grossWeight
    if (
      !weight ||
      !this.weightClassItemStore ||
      !this.visit ||
      !('vessels' in this.visit) ||
      !this.data.containerLength
    ) {
      return undefined
    }

    return this.weightClassItemStore.getWeightClass(
      weight,
      this.data.containerLength,
      this.visit.data.vesselServiceId,
    )
  }

  get visit() {
    return this.data.carrierVisitId
      ? this.itemStore.carrierVisitById[this.data.carrierVisitId]
      : undefined
  }

  get container() {
    return this.data.containerId && this.itemStore.containerById
      ? this.itemStore.containerById[this.data.containerId]
      : undefined
  }

  get railcarTrackPosition() {
    return this.railcarTrackPositionStore.elements[this.data.railcarTrackPositionId ?? -1]
  }

  get unreadOrderUpdates() {
    return _(this.orderUpdateStore?.elements)
      .filter(i => i.data.orderId === this.data.id)
      .value()
  }

  get notResolvedIssues() {
    return _(this.issueStore?.elements)
      .filter(e => !!this.data.notResolvedIssueIds?.includes(e.id))
      .value()
  }

  get hasNotResolvedIssues() {
    return (this.notResolvedIssues?.length ?? 0) > 0
  }

  get containerJourney() {
    if (!this.containerJourneyItemStore) return
    if (!this.data.containerId) return

    if (this.data.direction === CarrierVisitDirection.Inbound)
      return _(this.containerJourneyItemStore.journeysByInboundOrderId).get(this.data.id)
    else return _(this.containerJourneyItemStore.journeysByOutboundOrderId).get(this.data.id)
  }

  get hasMissingPosition() {
    return (
      !this.data.yardLocation &&
      !this.containerJourney?.data.plannedYardLocation &&
      this.data.direction === CarrierVisitDirection.Inbound
    )
  }

  get hasActiveHold() {
    if (this.data.holds !== undefined) {
      return this.data.holds.some(x => x.status === HoldStatus.Active)
    }
    return !!(this.data as { hasActiveHolds?: boolean }).hasActiveHolds
  }

  get activeHoldNames(): string[] {
    return (
      this.data.holds
        ?.filter(h => h.status === HoldStatus.Active)
        .map(h => h.holdType?.name)
        .filter((n): n is string => !!n) ??
      (this.data as { activeHolds?: ActiveHoldDto[] }).activeHolds
        ?.map(h => h.holdTypeName)
        .filter((n): n is string => !!n) ??
      []
    )
  }

  get isNonNumericAndCannotBeFulfilled() {
    const hasAssignedContainer = !!this.data.containerNumber

    return (
      !!this.data.nonNumericOrderId &&
      !hasAssignedContainer &&
      !this.data.nonNumericSuitableContainersCount
    )
  }

  get shipper() {
    if (!this.companyStore) return
    if (!this.data.shipperId) return

    return this.companyStore?.elements[this.data.shipperId]
  }

  get forwarder() {
    if (!this.companyStore) return
    if (!this.data.forwarderId) return

    return this.companyStore?.elements[this.data.forwarderId]
  }

  get journeyCategory() {
    if (this.containerJourney) return this.containerJourney.data.category
    return this.data.direction === CarrierVisitDirection.Inbound
      ? JourneyCategory.Import
      : JourneyCategory.Export
  }

  get canUnassignFromRelease() {
    if (this.containerJourney) return this.containerJourney.canUnassignFromRelease

    return this.data.status === OrderStatus.Open
  }

  setCrane = (crane?: IAssignedCraneForOrder, selectedCraneId?: number) => {
    this.assignedCraneName = crane?.craneName
    this.isAssignedCraneNotToSelected = crane?.craneId !== selectedCraneId
  }
}
